import React from 'react';
import { ArrowRight, ArrowLeft, Calendar, Tag } from 'lucide-react';

export const ARTICLES = [
  {
    slug: 'automatisierte-leistungsverzeichnisse-bauplanung',
    title: 'Wie automatisierte Leistungsverzeichnisse die Bauplanung verändern',
    description: 'Warum die Mengenermittlung aus Grundrissen das größte ungehobene Effizienz-Potenzial der Baubranche ist — und wie Automatisierung das ändert.',
    category: 'Digitalisierung',
    date: '2026-02-20',
    readingTime: '6 Min.',
    keywords: ['Leistungsverzeichnis automatisch erstellen', 'LV Software Bau', 'Bauplanung automatisieren'],
    body: [
      { h: 'Die unsichtbaren Stunden im Bau-Alltag' },
      'Wer in einem Bauunternehmen oder Architekturbüro arbeitet, kennt die Szene: Der Plan liegt auf dem Tisch, das Lineal daneben, ein Excel-Sheet ist offen — und stundenlang werden Wandlängen abgegriffen, Mengen multipliziert und Positionen getippt. Was als „kurze Mengenermittlung" angefangen hat, frisst am Ende einen halben Tag pro Geschoss.',
      { h: 'Warum das ein Branchenproblem ist' },
      'Diese Arbeit ist hochrepetitiv, fehleranfällig und liefert keinen Mehrwert für den Kunden. Sie ist ein klassisches Beispiel für eine Tätigkeit, die heute hervorragend von Software übernommen werden kann: strukturierte 2D-Daten in einem PDF, eine endliche Menge an Bauteil-Typen, klare Berechnungsregeln aus VOB/C DIN 18330. Genau das Profil, das ein Algorithmus zuverlässig abdeckt.',
      { h: 'Was sich ändert, wenn LV-Erstellung automatisiert wird' },
      'Drei Effekte werden in der Praxis sichtbar: erstens kürzere Angebotszyklen — was vorher zwei Tage dauerte, ist in einer Stunde fertig. Zweitens konsistentere Mengen: zwei Mitarbeiter erzeugen das gleiche LV, weil die Regel-Engine deterministisch ist. Drittens ein anderer Schwerpunkt der Arbeit: Architekten und Kalkulatoren verbringen mehr Zeit mit der inhaltlichen Bewertung — also dem, wofür sie ausgebildet sind — und weniger mit Lineal und Taschenrechner.',
      { h: 'Voraussetzungen für eine erfolgreiche Einführung' },
      'Damit Automatisierung wirklich zündet, braucht es drei Dinge: eine Engine, die mit echten Plänen umgeht (nicht nur mit Lehrbeispielen), eine transparente Mengen-Logik (nachvollziehbar bis auf jeden Quadratmeter) und einen ehrlichen Umgang mit Sonderfällen (Streifenfundament, Attika, Glasbaustein) — die werden von Hand ergänzt, mit Schnellrechner statt Stoppuhr.',
      { h: 'Fazit' },
      'Automatisierte Leistungsverzeichnisse sind kein Hype, sondern eine direkte Antwort auf eine seit Jahrzehnten ungelöste Tätigkeit im Büroalltag. Wer heute beginnt, gewinnt nicht nur Zeit — er gewinnt einen Wettbewerbsvorteil, den Mitbewerber ohne Software-Engine nicht aufholen.',
    ],
  },
  {
    slug: '5-fehler-lv-erstellung-vermeiden',
    title: '5 Fehler bei der LV-Erstellung — und wie man sie vermeidet',
    description: 'Die häufigsten Stolpersteine bei der manuellen Mengenermittlung — von vergessenen Wänden bis zu doppelt gezählten Räumen.',
    category: 'Praxis',
    date: '2026-02-12',
    readingTime: '5 Min.',
    keywords: ['LV automatisch erstellen', 'Leistungsverzeichnis Software', 'Mengenermittlung'],
    body: [
      { h: 'Fehler 1 — Wände zwischen Öffnungen vergessen' },
      'Klassischer Fall: Eine Wand wird zwischen einem Fenster und einer Tür „in zwei Stücke" zerlegt. Wer die Mengen manuell aus dem Plan abgreift, vergisst gerne den Bereich über dem Sturz und unter der Brüstung. Das führt zu zu kleinen Mauerwerks-Mengen — und am Ende zu Nachforderungen vom Maurer.',
      { h: 'Fehler 2 — Außen- und Innenwände vermischen' },
      'Ohne klare Klassifizierung landen Außen- und Innenwände in derselben Position. Spätestens wenn das WDVS hinzukommt, wird es schmerzhaft: dann fehlen Mengen für die Fassade, oder es werden Innenwände als Außenwände kalkuliert.',
      { h: 'Fehler 3 — Übermessungs-Regel von VOB/C ignorieren' },
      'VOB/C DIN 18330 schreibt vor: Öffnungen ≤ 2,5 m² werden übermessen (nicht abgezogen), Öffnungen > 2,5 m² werden abgezogen. Wer das in seiner Excel-Tabelle nicht sauber abbildet, baut systematische Über- oder Unterzählungen ein.',
      { h: 'Fehler 4 — Räume mehrfach gezählt' },
      'Wenn Bodenbelag, Estrich und Dämmung als getrennte Positionen entstehen und derselbe Raum dreimal abgegriffen wird, entstehen leicht Tippfehler — und am Ende stimmen die drei Mengen nicht überein.',
      { h: 'Fehler 5 — Volumen ohne Wandhöhen-Standard' },
      'Wandvolumen ist Wandfläche × Wandstärke. Aber: welche Wandhöhe nehmen wir an? Lichte Höhe? Geschosshöhe von OK Rohdecke bis UK Rohdecke? Wer das pro Projekt anders macht, produziert ein nicht-vergleichbares LV.',
      { h: 'Wie man alle fünf Fehler in einem Schritt loswird' },
      'Eine Software-Engine wendet die Regeln deterministisch an. Wenn die Logik einmal stimmt, stimmt sie für immer — über alle Projekte hinweg, unabhängig vom Bearbeiter. Das ist der Hauptunterschied zwischen einem manuellen LV und einem automatisierten LV.',
    ],
  },
  {
    slug: 'digitalisierung-im-bauwesen-chancen-trends',
    title: 'Digitalisierung im Bauwesen: Chancen und Trends',
    description: 'BIM, automatisierte Mengenermittlung: Was 2026 in der Bau-IT funktioniert und was Hype-Marketing bleibt.',
    category: 'Trends',
    date: '2026-02-05',
    readingTime: '7 Min.',
    keywords: ['Digitalisierung Bauwesen', 'Ausschreibung digitalisieren', 'Bauplanung automatisieren'],
    body: [
      { h: 'Die Lage 2026 — viel Bewegung, wenig echte Software' },
      'Auf Konferenzen ist die Bauwirtschaft längst „digital". In den Büros sieht es oft anders aus: PDF-Pläne werden ausgedruckt, mit dem Lineal abgegriffen und Mengen in Excel getippt. Diese Lücke zwischen Marketing und Realität ist die größte Chance für spezialisierte Software-Anbieter.',
      { h: 'Trend 1 — Vom BIM-Modell zur 2D-Realität' },
      'Building Information Modeling ist das große Versprechen, aber in der Praxis arbeiten 80 % der Projekte weiterhin mit 2D-PDF-Plänen — auch weil viele Bestandsobjekte nie als 3D-Modell existiert haben. Der pragmatische Weg ist daher nicht „BIM überall", sondern „2D-Pläne mit Software-Engines aufwerten".',
      { h: 'Trend 2 — Automatische Plan-Analyse' },
      'Moderne Software-Engines können Pläne lesen wie ein Mensch — sie verstehen Schraffuren, Bemaßungen und Symbolik. Kombiniert mit klassischer Computer-Vision-Pipeline (Vektoren, Pixel, Geometrie) entsteht eine Engine, die robust mit echten Plänen umgeht, nicht nur mit Lehrbüchern.',
      { h: 'Trend 3 — Mengenermittlung als API' },
      'Die Zukunft sind APIs, die einen Plan entgegennehmen und ein strukturiertes JSON mit Räumen, Wänden, Öffnungen und Mengen zurückgeben. Daraus lassen sich LVs, Kostenkalkulationen und Ausschreibungen automatisch erzeugen — und in jede ERP-Software integrieren.',
      { h: 'Was funktioniert, was bleibt Hype' },
      'Funktioniert: deterministische Mengenermittlung, automatische Öffnungs-Klassifizierung, automatische DIN-Snaps, VOB/C-konforme LV-Erstellung. Hype: vollautomatische 3D-Modellierung aus 2D-Plänen, autonome Projektplanung. Letzteres ist nicht falsch — aber 2026 noch nicht produktionsreif.',
      { h: 'Fazit' },
      'Wer heute mit pragmatischer 2D-Plan-Automatisierung beginnt, baut eine Datenbasis auf, die in fünf Jahren der Standard sein wird. Die Frage ist nicht „ob", sondern „wann" diese Tools in jedem Bauunternehmen Standard sind.',
    ],
  },
  {
    slug: 'automatisierte-grundrissanalyse-zeit-sparen',
    title: 'Warum automatisierte Grundrissanalyse Zeit spart',
    description: 'Konkrete Rechnung: Wie viel Zeit ein Architekturbüro pro Jahr sparen kann, wenn die Mengenermittlung automatisiert wird.',
    category: 'ROI',
    date: '2026-01-28',
    readingTime: '4 Min.',
    keywords: ['Grundriss in Leistungsverzeichnis', 'LV Software Bau', 'Bauplanung automatisieren'],
    body: [
      { h: 'Die Zahlen, mit denen wir rechnen' },
      'Ein typisches Einfamilienhaus mit 170 m² Wohnfläche enthält rund 50–60 Wandsegmente, 20–30 Öffnungen und 8–12 Räume. Eine erfahrene Kalkulator-Kraft braucht für die manuelle Mengenermittlung eines Geschosses zwischen vier und acht Stunden.',
      { h: 'Was bedeutet das pro Jahr?' },
      'Bei 30 Projekten im Jahr und durchschnittlich zwei Geschossen pro Projekt sind das 240 bis 480 Stunden — also 30 bis 60 Personentage. Bei einem Stundensatz von 65 € entspricht das 15.000 € bis 31.000 € jährlichen Personalkosten allein für Mengenrechnen.',
      { h: 'Der automatisierte Workflow' },
      'Mit LAIzyBlueprint reduziert sich die Mengenermittlung pro Geschoss auf wenige Minuten: Plan hochladen, Engine analysiert, Ergebnis prüfen, manuelle Sonderpositionen ergänzen, LV-PDF exportieren. Realistisch: 15 Minuten statt 5 Stunden.',
      { h: 'Was Sie mit der gewonnenen Zeit machen' },
      'Die spannende Frage ist nicht „wie viel Zeit sparen wir", sondern „was machen wir mit der Zeit". Mehr Projekte annehmen? Mehr Zeit für Entwurfsqualität? Pünktlich Feierabend? Jede dieser Antworten ist legitim — und alle drei sind ein besseres Geschäftsmodell als ein Mitarbeiter, der zwei Tage pro Woche Wandlängen abgreift.',
      { h: 'Probieren statt rechnen' },
      'Die ehrlichste Methode, sich diese Zahlen anzusehen, ist: einen echten Plan hochzuladen und das Ergebnis mit dem zu vergleichen, was Ihr Büro heute macht. Die Engine liefert in unter einer Minute — die Vergleichszahl haben Sie schon.',
    ],
  },
];

export function BlogList({ navigate }) {
  return (
    <article className="pt-28 pb-24 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Blog</div>
        <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Wissensbereich.
        </h1>
        <p className="text-lg text-zinc-400 leading-relaxed mb-12 max-w-2xl">
          Praxisnahe Artikel zu Digitalisierung, Mengenermittlung und Software für die Bauplanung —
          geschrieben von Menschen, die selbst lange genug am Reißbrett saßen.
        </p>

        <div className="space-y-3">
          {ARTICLES.map((a) => (
            <button
              key={a.slug}
              onClick={() => navigate(`blog/${a.slug}`)}
              data-testid={`blog-card-${a.slug}`}
              className="w-full text-left p-6 bg-zinc-900/50 border border-zinc-800/60 rounded-xl hover:border-zinc-700 hover:bg-zinc-900 transition-all group"
            >
              <div className="flex items-center gap-3 mb-3 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400">
                  <Tag className="w-3 h-3" /> {a.category}
                </span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {formatDate(a.date)}</span>
                <span>·</span>
                <span>{a.readingTime}</span>
              </div>
              <h2 className="text-xl font-bold mb-2 text-white group-hover:text-blue-400 transition-colors leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {a.title}
              </h2>
              <p className="text-zinc-400 text-sm leading-relaxed">{a.description}</p>
              <div className="mt-3 inline-flex items-center gap-2 text-blue-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Lesen <ArrowRight className="w-4 h-4" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </article>
  );
}

export function BlogArticle({ slug, navigate, onRegister }) {
  const a = ARTICLES.find((x) => x.slug === slug);
  if (!a) {
    return (
      <article className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center">
        <h1 className="text-3xl font-bold mb-4">Artikel nicht gefunden</h1>
        <button onClick={() => navigate('blog')} className="text-blue-400 hover:text-blue-300">← Zurück zur Übersicht</button>
      </article>
    );
  }
  return (
    <article className="pt-28 pb-24 px-6" data-testid={`article-${slug}`}>
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate('blog')} className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-300 text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Alle Artikel
        </button>
        <div className="flex items-center gap-3 mb-4 text-xs text-zinc-500">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400">
            <Tag className="w-3 h-3" /> {a.category}
          </span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {formatDate(a.date)}</span>
          <span>·</span>
          <span>{a.readingTime}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight tracking-tight mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {a.title}
        </h1>
        <p className="text-lg text-zinc-400 leading-relaxed mb-10">{a.description}</p>

        <div className="prose prose-invert prose-zinc max-w-none">
          {a.body.map((b, i) =>
            typeof b === 'string' ? (
              <p key={i} className="text-zinc-300 leading-relaxed mb-4">{b}</p>
            ) : (
              <h2 key={i} className="text-2xl font-bold mt-10 mb-4 text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {b.h}
              </h2>
            )
          )}
        </div>

        <div className="mt-14 pt-10">
          <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Bereit, das selbst zu erleben?
          </h3>
          <p className="text-zinc-400 mb-6">Laden Sie Ihren ersten Plan hoch — die erste Analyse ist kostenlos.</p>
          <button onClick={onRegister} className="px-7 py-3 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg flex items-center gap-2 text-sm">
            Jetzt testen <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </article>
  );
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}
